import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InsuranceTableComponent } from '../../../componentes/insurance-table/insurance-table.component';
import { ExportarComponent } from '../../../componentes/exportar/exportar.component';
import { CamposComponent } from '../../../componentes/campos/campos.component';
import { TableConfig } from '../../../interfaces/table-config.interface';
import { CarteraFilter } from '../../../interfaces/filters.interfaces';
import {
  PolicyRequest,
  PolicyResponse,
  PolicyData,
} from '../../../interfaces/micartera.interfaces';
import { ApiResponse } from '../../../interfaces/common.interfaces';
import { MicarteraService } from '../../../services/micartera.service';
import { AuthService } from '../../../services/auth.service';
import { HttpResponse } from '@angular/common/http';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytics.service';

/**
 * Componente para gestionar la cartera de pólizas
 * Permite visualizar y exportar información sobre las pólizas del productor
 */
@Component({
  selector: 'app-micartera',
  standalone: true,
  imports: [
    CommonModule,
    InsuranceTableComponent,
    ExportarComponent,
    CamposComponent,
  ],
  templateUrl: './micartera.component.html',
  styleUrls: ['./micartera.component.css'],
})
export class MicarteraComponent {
  insuranceData: PolicyResponse[] = [];
  mensajeNoData: string = 'Selecciona los campos que deseas buscar';
  errorStatus?: number;
  fecha1?: Date;
  pageNumber: number = 1;
  pageSize: number = 6;
  totalRecordsFromServer: number = 0;
  totalPagesFromServer: number = 0;
  /**
   * Totales del servidor para la tabla. Usa `totalPremium` como total de la columna Prima.
   */
  tableTotals?: { [key: string]: number };
  private lastFilters?: CarteraFilter;

  // Configuración de la tabla
  tableConfig: TableConfig = {
    columns: [
      {
        key: 'policyNumber',
        label: 'Póliza',
        sortable: true,
        type: 'poliza',
      },
      {
        key: 'lob',
        label: 'Ramo',
        sortable: true,
      },
      {
        key: 'slobDescription',
        label: 'Subramo',
        sortable: true,
      },
      {
        key: 'insuredCode',
        label: 'Código',
        sortable: true,
      },
      {
        key: 'clientName',
        label: 'Asegurado',
        sortable: true,
      },
      {
        key: 'effectiveDate',
        label: 'Vigencia',
        sortable: true,
        type: 'date', // Mantenemos fecha como texto
      },
      {
        key: 'premium',
        label: 'Prima',
        sortable: true,
        type: 'currency', // Para mostrar como 10,000.00 DOP
      },
      {
        key: 'insuredAmmount',
        label: 'Monto Asegurado',
        sortable: true,
        type: 'currency', // Para mostrar como 10,000.00 DOP
      },
      {
        key: 'pendingBalance',
        label: 'Balance Pendiente',
        sortable: true,
        type: 'currency', // Para mostrar como 10,000.00 DOP
      },

      {
        key: 'coinsurance',
        label: 'Coseguro',
        sortable: true,
        type: 'percentage', // Para mostrar como 100.00%
      },
    ],
    showTotals: true,
    totalFields: ['premium'],
  };

  opcionesSelectFilter = [
    { id: 'poliza', nombre: 'Póliza' },
    { id: 'codigo', nombre: 'Código' },
    { id: 'cedula', nombre: 'Cédula' },
    { id: 'rnc', nombre: 'RNC' },
    { id: 'nombre', nombre: 'Nombre' },
  ];

  // Referencia al componente de la tabla
  @ViewChild(InsuranceTableComponent) tablaComponent!: InsuranceTableComponent;

  title: string = '';

  /**
   * Constructor del componente
   * @param micarteraService Servicio para obtener datos de la cartera
   * @param authService Servicio de autenticación
   */
  constructor(
    private micarteraService: MicarteraService,
    private authService: AuthService,
    private gAnalytics:GoogleAnalyticsService
  ) {
    this.title = 'Mi cartera';
  }

  ngOnInit(): void {}

  /**
   * Carga los datos iniciales de la cartera sin filtros
   * Realiza una consulta al servidor para obtener todas las pólizas del productor
   */
  loadInitialData(): void {
    this.pageNumber = 1;
    const request: PolicyRequest = {
      producerNumber: this.authService.getProducerNumber() || 0,
      dateTo: new Date(),
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
    };

    this.micarteraService.getPolicies(request).subscribe({
      next: (response: HttpResponse<ApiResponse<PolicyData>>) => {
        if (response.status === 200) {
          this.insuranceData = response.body?.data?.policies || [];
          this.totalRecordsFromServer =
            response.body?.data?.pagination?.totalRecords || 0;
          this.totalPagesFromServer =
            response.body?.data?.pagination?.totalPages || 0;
          this.tableTotals = {
            premium: response.body?.data?.pagination?.totalPremium ?? 0,
          };
          this.errorStatus = undefined;
        } else if (response.status === 204) {
          this.insuranceData = [];
          this.tableTotals = undefined;
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al obtener las pólizas:', error);
        this.insuranceData = [];
        this.tableTotals = undefined;
        this.mensajeNoData = 'No se han podido obtener los datos de la cartera';
      },
    });
  }

  /**
   * Maneja los cambios en los filtros de búsqueda
   * Realiza una nueva consulta al servidor con los filtros actualizados
   * @param filters Objeto con los filtros seleccionados por el usuario
   */
  onFilterChange(filters: CarteraFilter): void {
    if (Object.keys(filters).length === 0) {
      this.insuranceData = [];
      this.tableTotals = undefined;
      this.mensajeNoData = 'Selecciona los campos que deseas buscar';
      this.errorStatus = undefined;
      return;
    }
    // Se ajusta la fecha para que coincida luego de la transformación
    this.fecha1 = filters.dateTo!;
    this.lastFilters = filters;
    this.pageNumber = 1;

    const request: PolicyRequest = {
      producerNumber: this.authService.getProducerNumber() || 0,
      dateTo: filters.dateTo || new Date(),
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      ...(filters?.policyNumber && { policy: filters.policyNumber }),
      ...(filters?.clientCode && { ClientCode: filters.clientCode }),
    };

    // Agregar campos opcionales solo si tienen valor
    if (filters.lobId) request.lob = filters.lobId;
    if (filters.sLobId) request.slob = filters.sLobId;
    if (filters.clientCode) request.ClientCode = filters.clientCode;

    if (filters.clientCode) {
    }
    this.micarteraService.getPolicies(request).subscribe({
      next: (response: HttpResponse<ApiResponse<PolicyData>>) => {
        if (response.status === 200) {
          this.insuranceData = response.body?.data?.policies || [];
          this.totalRecordsFromServer =
            response.body?.data?.pagination?.totalRecords || 0;
          this.totalPagesFromServer =
            response.body?.data?.pagination?.totalPages || 0;
          this.tableTotals = {
            premium: response.body?.data?.pagination?.totalPremium ?? 0,
          };
          this.errorStatus = undefined;
        } else if (response.status === 204) {
          this.insuranceData = [];
          this.tableTotals = undefined;
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al filtrar las pólizas:', error);
        this.insuranceData = [];
        this.tableTotals = undefined;
        this.mensajeNoData =
          'Ha ocurrido un error en el servidor al obtener las pólizas';
      },
    });
  }

  /**
   * Solicita al API la página seleccionada en la tabla
   * @param page Número de página a consultar
   */
  onPageChange(page: number): void {
    this.pageNumber = page;
    const filters = this.lastFilters;

    const request: PolicyRequest = {
      producerNumber: this.authService.getProducerNumber() || 0,
      dateTo: filters?.dateTo || this.fecha1 || new Date(),
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      ...(filters?.policyNumber && { policy: filters.policyNumber }),
      ...(filters?.clientCode && { ClientCode: filters.clientCode }),
    };

    if (filters?.lobId) request.lob = filters.lobId;
    if (filters?.sLobId) request.slob = filters.sLobId;
    if (filters?.clientCode) request.ClientCode = filters.clientCode;

    this.micarteraService.getPolicies(request).subscribe({
      next: (response: HttpResponse<ApiResponse<PolicyData>>) => {
        if (response.status === 200) {
          this.insuranceData = response.body?.data?.policies || [];
          this.totalRecordsFromServer =
            response.body?.data?.pagination?.totalRecords || 0;
          this.totalPagesFromServer =
            response.body?.data?.pagination?.totalPages || 0;
          this.tableTotals = {
            premium: response.body?.data?.pagination?.totalPremium ?? 0,
          };
          this.errorStatus = undefined;
        } else if (response.status === 204) {
          this.insuranceData = [];
          this.tableTotals = undefined;
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al filtrar las pólizas:', error);
        this.insuranceData = [];
        this.tableTotals = undefined;
        this.mensajeNoData =
          'Ha ocurrido un error en el servidor al obtener las pólizas';
      },
    });
  }

  /**
   * Exporta todos los registros del reporte según los filtros actuales
   * Consulta el total de pólizas al API y las usa solo para la exportación
   * @param formato Formato de exportación deseado
   */
  onExportar(formato: string): void {
    if (!this.tablaComponent || this.totalRecordsFromServer === 0) {
      return;
    }

    const headerInfo = {
      titulo: 'Mi Cartera',
      lob: this.getLobName(),
      slob: this.getSlobName(),
      fecha1: this.fecha1!,
      tableConfig: this.tableConfig,
    };

    this.gAnalytics.trackEvent('cartera_exportar', {
      page: '/cartera',
      format: formato,
    });

    const filters = this.lastFilters;
    const request: PolicyRequest = {
      producerNumber: this.authService.getProducerNumber() || 0,
      dateTo: filters?.dateTo || this.fecha1 || new Date(),
      pageNumber: 1,
      pageSize: this.totalRecordsFromServer,
      ...(filters?.policyNumber && { policy: filters.policyNumber }),
      ...(filters?.clientCode && { ClientCode: filters.clientCode }),
    };

    if (filters?.lobId) request.lob = filters.lobId;
    if (filters?.sLobId) request.slob = filters.sLobId;
    if (filters?.clientCode) request.ClientCode = filters.clientCode;

    this.micarteraService.getPolicies(request).subscribe({
      next: (response: HttpResponse<ApiResponse<PolicyData>>) => {
        if (response.status !== 200) {
          return;
        }

        const allData = response.body?.data?.policies || [];
        const dataOriginal = this.tablaComponent.data;
        this.tablaComponent.data = allData;
        this.tablaComponent.exportarDatos(formato, headerInfo);
        this.tablaComponent.data = dataOriginal;
      },
      error: (error) => {
        console.error('Error al obtener las pólizas para exportar:', error);
      },
    });
  }

  // Extrayendo los nombres de los ramos y subramos
  @ViewChild(CamposComponent) camposComponent!: CamposComponent;

  /**
   * Obtiene el nombre del ramo (LOB) seleccionado
   * @returns Nombre del ramo o cadena vacía si no hay selección
   */
  public getLobName(): string {
    const selectedLob = this.camposComponent?.lobs.find(
      (lob) => lob.lobId === Number(this.camposComponent?.selectedLob)
    );
    return selectedLob?.lobName || '';
  }

  /**
   * Obtiene el nombre del subramo (SLOB) seleccionado
   * @returns Nombre del subramo o cadena vacía si no hay selección
   */
  public getSlobName(): string {
    const selectedSLob = this.camposComponent?.filteredSLobs.find(
      (slob) => slob.sLobId === Number(this.camposComponent?.selectedSLob)
    );
    return selectedSLob?.sLobName || '';
  }
}
